import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Info, type LucideIcon, XCircle } from 'lucide-react';
import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

const alertVariants = cva(
  'relative flex w-full gap-3 rounded-md border p-4 text-sm',
  {
    variants: {
      variant: {
        default: 'border-border bg-surface text-fg',
        info: 'border-info/30 bg-info-subtle text-fg',
        success: 'border-success/30 bg-success-subtle text-fg',
        warning: 'border-warning/30 bg-warning-subtle text-fg',
        danger: 'border-danger/30 bg-danger-subtle text-fg',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

const ICONS: Record<NonNullable<VariantProps<typeof alertVariants>['variant']>, LucideIcon | null> = {
  default: null,
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
};

const ICON_COLORS: Record<NonNullable<VariantProps<typeof alertVariants>['variant']>, string> = {
  default: '',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

export interface AlertProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  icon?: LucideIcon | null;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, icon, children, ...props }, ref) => {
    const Icon = icon === undefined ? ICONS[variant ?? 'default'] : icon;
    return (
      <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
        {Icon ? (
          <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', ICON_COLORS[variant ?? 'default'])} aria-hidden />
        ) : null}
        <div className="flex-1">{children}</div>
      </div>
    );
  },
);
Alert.displayName = 'Alert';

export const AlertTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h4
      ref={ref}
      className={cn('mb-1 text-sm font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
AlertTitle.displayName = 'AlertTitle';

export const AlertDescription = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-sm text-fg-secondary', className)} {...props} />
  ),
);
AlertDescription.displayName = 'AlertDescription';
